import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectFilteredVirtualCards,
  selectUsersFullPaginatedDataFromState,
  selectVirtualCardsFullPaginatedData,
} from 'src/app/VirtualCardsYourCards/redux/virtualCardsYourCards.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import VirtualCardsBoxAndLinks, {
  VirtualCardsAnalyticsEventProperties,
  VirtualCardsFiltersBody,
} from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { OrganizationRoles } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch, useQueryParamArrayHandler } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { postReportVirtualCardsDownload } from 'src/redux/actions/data/reportDownloads.actions'
import { getVirtualCards } from 'src/redux/actions/data/virtualCards.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectVirtualCardsActiveRecipientMeCountStatisticsFromState,
  selectVirtualCardsRecipientMeCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const VirtualCardsYourCards: React.FC = () => {
  const translate = useTranslate()

  const virtualCardsFullPaginatedData = useSelector(
    selectVirtualCardsFullPaginatedData,
  )
  const { analytics } = useAnalyticsContext()
  const filteredVirtualCards = useSelector(selectFilteredVirtualCards)
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const virtualCardsRecipientMeCount = useSelector(
    selectVirtualCardsRecipientMeCountStatisticsFromState,
  )
  const virtualCardsActiveRecipientMeCount = useSelector(
    selectVirtualCardsActiveRecipientMeCountStatisticsFromState,
  )
  const usersFullPaginatedData = useSelector(
    selectUsersFullPaginatedDataFromState,
  )
  const appName = useSelector(selectAppName)
  const queryParams = useSelector(selectQueryParams)
  const user = useSelector(selectUserListModelFromState)
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isGuestUser =
    showGuestRole && user?.organizationRole === OrganizationRoles.guest
  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  const dispatch = useBackgroundDispatch()

  const [selectedFilters, setSelectedFilters] = useState<
    VirtualCardsAnalyticsEventProperties | undefined
  >(undefined)

  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 130)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 130)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.VirtualCardsRecipientMeCountStatistics,
      appStatisticsKeys.VirtualCardsActiveRecipientMeCountStatistics,
    ])
  }, [dispatchStatistics])

  const dispatchPostReportVirtualCardsDownload = (
    filters: VirtualCardsFiltersBody,
  ): void => {
    dispatch(
      postReportVirtualCardsDownload({
        ...filters,
        recipient: 'me',
      }),
    )
    analytics?.track(
      'virtual cards - your cards - download all your virtual cards',
    )
  }

  const fetchVirtualCards = (
    filtersBody?: VirtualCardsFiltersBody,
    page?: number,
  ): void => {
    dispatch(
      getVirtualCards({
        count: 10,
        page: page,
        ...filtersBody,
        recipient: 'me',
      }),
    )
  }

  const infiniteScrollProps = useMemo(
    () => ({
      ...virtualCardsFullPaginatedData,
      fetch: fetchVirtualCards,
    }),
    [virtualCardsFullPaginatedData, fetchVirtualCards],
  )

  // analytics
  useEffect(() => {
    if (selectedFilters) {
      analytics?.track('your cards filter applied', {
        status: selectedFilters.status && selectedFilters.status.length > 0,
        search: !!selectedFilters.search,
        card_type:
          selectedFilters.card_type && selectedFilters.card_type.length > 0,
        deactivating_range: !!selectedFilters.deactivating_in,
      })
    }
  }, [selectedFilters])

  return !statisticsLoaded.virtualCardsRecipientMeCountStatisticsLoaded ||
    !statisticsLoaded.virtualCardsActiveRecipientMeCountStatisticsLoaded ? (
    <LoadingBackgroundPage />
  ) : (
    <>
      <Helmet>
        <title>{`Your Virtual Cards | ${appName}`}</title>
        <meta
          name="description"
          content={`Your Virtual Cards page of ${appName}`}
        />
      </Helmet>
      <div
        css={css`
          height: 100%;
        `}
        ref={ref}
        id={'ref'}
      >
        <BackgroundPageContent noStackingIndex>
          <VirtualCardsBoxAndLinks
            onDownloadClick={dispatchPostReportVirtualCardsDownload}
            defaultTitle={
              <ObjectBoxTitle
                title={translate('Your Cards')}
                static={{
                  label: translate('Active'),
                  count: virtualCardsActiveRecipientMeCount,
                }}
              />
            }
            isYourCards
            virtualCards={filteredVirtualCards}
            emptyStateDesign={TableEmptyStateDesigns.YourVirtualCardsActive}
            infiniteScrollProps={infiniteScrollProps}
            initialFilterValues={{
              sortBy: 'pendingUpdatedAt',
              keyword: queryParams.search || '',
              statuses: useQueryParamArrayHandler(queryParams.statuses),
              cardTypes: useQueryParamArrayHandler(queryParams.cardTypes),
              deactivatingIn: queryParams.deactivatingIn,
            }}
            totalCount={virtualCardsRecipientMeCount}
            usersFullPaginatedData={usersFullPaginatedData}
            setSelectedFilters={setSelectedFilters}
            readOnly={isGuestUser}
            clientHeight={clientHeight || 0}
          />
        </BackgroundPageContent>
      </div>
    </>
  )
}

export default VirtualCardsYourCards
